/**
 * @file 「計画のもと」の出題をまとめて読み、単元・学校から引ける形にする hook。
 *
 * @what: kind = reference の資料すべての scan_items を取り、語彙 (`分野/単元`) と出典校の
 *   索引、単元 id ごとの出題を作る。計画の面で単元行の件数を出すために使う。
 * @why: 索引は暗号化 payload の中にあるためサーバ側では絞り込めない。冊数も件数も
 *   家庭規模 (1 冊 380 問 ≈ 200KB) なので、まとめて読んで手元で引く方が単純で速い。
 *   `enabled` を false にしている間は読まないので、必要な面を開くまで通信しない。
 * @failure: 1 冊でも失敗したら `error` (部分的な索引を出すと「出題が無い単元」と
 *   見分けがつかないため)。詳細は console のみ。
 */

import { useEffect, useMemo, useState } from "react";
import { fetchScanItems } from "../api/scans";
import type { StudySource } from "../api/sources";
import {
  entriesByUnitId,
  indexBySchool,
  indexByTaxonomy,
  type CorpusEntry,
} from "../model/corpusIndex";
import { attainmentModel } from "../model";

export interface StudyCorpus {
  entries: CorpusEntry[];
  /** unit.id → その単元の出題 (ページ順)。 */
  byUnitId: Record<string, CorpusEntry[]>;
  /** 出典校 → 出題。件数の多い順に並べたい場合は呼び出し側で並べ替える。 */
  bySchool: Map<string, CorpusEntry[]>;
}

export type StudyCorpusState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; corpus: StudyCorpus }
  | { status: "error" };

const EMPTY_CORPUS: StudyCorpus = { entries: [], byUnitId: {}, bySchool: new Map() };

export function useStudyCorpus(
  familyId: string,
  sources: StudySource[] | null,
  enabled: boolean,
): StudyCorpusState {
  const [entriesState, setEntriesState] = useState<
    { status: "idle" } | { status: "loading" } | { status: "ready"; entries: CorpusEntry[] } | { status: "error" }
  >({ status: "idle" });

  // 依存を id の連結にして、sources 配列の同一性で読み直さないようにする。
  const referenceSources = useMemo(
    () => (sources ?? []).filter((s) => s.kind === "reference"),
    [sources],
  );
  const referenceKey = referenceSources.map((s) => s.id).join(",");

  useEffect(() => {
    if (!enabled || sources === null) {
      setEntriesState({ status: "idle" });
      return;
    }
    if (referenceSources.length === 0) {
      setEntriesState({ status: "ready", entries: [] });
      return;
    }
    const controller = new AbortController();
    setEntriesState({ status: "loading" });
    Promise.all(
      referenceSources.map(async (s) => {
        const items = await fetchScanItems(familyId, s.id, controller.signal);
        return items.map((item) => ({ item, sourceId: s.id, sourceTitle: s.title }));
      }),
    )
      .then((perSource) => {
        if (!controller.signal.aborted) {
          setEntriesState({ status: "ready", entries: perSource.flat() });
        }
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[study] failed to load corpus", err);
        setEntriesState({ status: "error" });
      });
    return () => controller.abort();
    // referenceKey が実質の依存 (referenceSources は毎回別配列になりうる)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyId, enabled, sources === null, referenceKey]);

  return useMemo<StudyCorpusState>(() => {
    if (entriesState.status !== "ready") return entriesState;
    if (entriesState.entries.length === 0) return { status: "ready", corpus: EMPTY_CORPUS };
    const byTaxonomy = indexByTaxonomy(entriesState.entries);
    return {
      status: "ready",
      corpus: {
        entries: entriesState.entries,
        byUnitId: entriesByUnitId(attainmentModel, byTaxonomy),
        bySchool: indexBySchool(entriesState.entries),
      },
    };
  }, [entriesState]);
}
